import {
  Alert,
  Avatar,
  Button,
  Card,
  Group,
  List,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { AvailableTime } from '../../../../types/AvailabilityModels';
import { IconInfoCircle } from '@tabler/icons-react';
import { primaryColor } from '../../../../constants/theme';
import { useDisclosure } from '@mantine/hooks';
import { format } from 'date-fns';
import { ErrorModal } from '../error/ErrorModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { requestBooking } from '../../../../services/BookingService';
import { useApp } from '../../../../context/AppContext';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageRoutes } from '../../../../constants/routes';
import { getProfessionalProfile } from '../../../../services/ProfileService';
import { getProfilePicture } from '../../../../types/UserDocument';
import { ProfileResponseModel } from '../../../../types/Profile';
import { TopicSlotModel } from '../../../../types/TopicModels';
import { TopicSelection } from './TimeSelector';

interface BookAppointmentsProps {
  professionalId: string;
  selectedTopicSelections?: TopicSelection[];
  selectedTime?: AvailableTime | null;
  selectedTimes?: AvailableTime[];
  confirmedTopic?: TopicSlotModel | null;
  onClearSelection?: () => void;
  professionalTimeZone?: string;
}

export const areSelectedTimesConsecutive = (
  selectedTimes: AvailableTime[]
): boolean => {
  if (selectedTimes.length <= 1) {
    return true;
  }

  const sortedTimes = [...selectedTimes].sort(
    (a, b) => new Date(a.startIso).getTime() - new Date(b.startIso).getTime()
  );

  return sortedTimes.every((time, index) => {
    if (index === 0) {
      return true;
    }

    const previousTime = sortedTimes[index - 1];
    return (
      new Date(time.startIso).getTime() ===
      new Date(previousTime.endIso).getTime()
    );
  });
};

export function getTimeInZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

export const BookAppointmentForm = ({
  professionalId,
  selectedTopicSelections = [],
  selectedTime,
  selectedTimes,
  confirmedTopic,
  onClearSelection,
}: BookAppointmentsProps) => {
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const [message, setMessage] = useState<string>('');
  const { token, profile } = useApp();

  const { data: professionalProfile } = useQuery({
    queryKey: ['professionalProfile', professionalId],
    enabled: !!professionalId,
    queryFn: async () => {
      return getProfessionalProfile(professionalId);
    },
  });

  const normalizedTopicSelections = useMemo(() => {
    if (selectedTopicSelections.length > 0) {
      return selectedTopicSelections;
    }

    const legacyTimes =
      selectedTimes && selectedTimes.length > 0
        ? selectedTimes
        : selectedTime
          ? [selectedTime]
          : [];

    if (legacyTimes.length === 0) {
      return [];
    }

    return [
      {
        topicKey: confirmedTopic?.id ? `topic-${confirmedTopic.id}` : 'open-session',
        topic: confirmedTopic ?? null,
        times: legacyTimes,
      },
    ];
  }, [selectedTopicSelections, selectedTimes, selectedTime, confirmedTopic]);

  const selectedTopicEntries = useMemo(() => {
    return normalizedTopicSelections.flatMap((selection) =>
      selection.times.map((time) => ({
        topic: selection.topic,
        topicKey: selection.topicKey,
        time,
      }))
    );
  }, [normalizedTopicSelections]);

  const effectiveSelectedTime = selectedTopicEntries[0]?.time ?? null;
  const selectedDate = effectiveSelectedTime
    ? new Date(effectiveSelectedTime.startIso)
    : new Date();

  const slotRate = useMemo(() => {
    return selectedTopicEntries.reduce(
      (total, entry) => total + (entry.time.rate ?? entry.topic?.rate ?? 0),
      0
    );
  }, [selectedTopicEntries]);

  const selectedCount = selectedTopicEntries.length;

  const groupedConfirmationSelections = useMemo(() => {
    return normalizedTopicSelections
      .filter((selection) => selection.times.length > 0)
      .map((selection) => ({
        topicKey: selection.topicKey,
        topic: selection.topic,
        times: [...selection.times].sort(
          (a, b) =>
            new Date(a.startIso).getTime() - new Date(b.startIso).getTime()
        ),
      }));
  }, [normalizedTopicSelections]);

  const placeholder =
    groupedConfirmationSelections.length === 1 &&
    groupedConfirmationSelections[0].topic &&
    !groupedConfirmationSelections[0].topic.isGeneric
      ? `Any notes for your ${groupedConfirmationSelections[0].topic.title} session?`
      : 'Would you like to add a message?';

  const { mutate, isPending, isError, reset } = useMutation({
    mutationKey: [
      'book-appointment',
      professionalId,
      selectedCount,
    ],
    mutationFn: async () => {
      if (!selectedTopicEntries.length) {
        throw new Error('No selected time');
      }

      await requestBooking(
        {
          userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          requestId: generateUUID(),
          availabilityId: selectedTopicEntries[0].time.id ?? 0,
          userId: profile?.id ?? '',
          userName: profile?.fullName ?? '',
          professionalId,
          professionalName: professionalProfile?.fullName ?? '',
          bookingDate: format(selectedDate, 'yyyy-MM-dd'),

          bookingTimes: selectedTopicEntries
            .filter((entry) => typeof entry.time.id === 'number')
            .map((entry) => ({
              id: entry.time.id as number,
              startTime: getTimeInZone(
                new Date(entry.time.startIso),
                Intl.DateTimeFormat().resolvedOptions().timeZone
              ),
              endTime: getTimeInZone(
                new Date(entry.time.endIso),
                Intl.DateTimeFormat().resolvedOptions().timeZone
              ),
              rate: entry.time.rate ?? entry.topic?.rate,
              TopicId: entry.topic?.id,
            })),
          message,
        },
        token || ''
      );
    },
    onError: (error) => {
      close();
      console.error('Error booking appointment:', error);
    },
    onSuccess: () => {
      close();
      navigate(PageRoutes.BOOKINGS);
    },
  });

  const handleBookAppointment = () => {
    open();
  };

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage="Error booking appointment, please try again"
        dismissAction={reset}
        retryAction={open}
        isLoading={isPending}
      />
      <BookingAppointmentConfirmationModal
        professionalProfile={professionalProfile ?? null}
        opened={opened}
        close={close}
        selectedTopicSelections={groupedConfirmationSelections}
        selectedDate={selectedDate}
        isLoading={isPending}
        totalCost={slotRate}
        onConfirm={mutate}
      />
      <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Group gap={5}>
              <Title ta="start" fw="normal" order={5}>
                Message
              </Title>
              <Tooltip label="Optional: Add any specific requests or notes for the creator">
                <IconInfoCircle size={22} color={primaryColor} />
              </Tooltip>
            </Group>
            <Group gap={5}>
              <Title ta="start" fw="normal" order={5}>
                Total:
              </Title>
              <Text c={primaryColor} ta="start">
                £{slotRate}
              </Text>
            </Group>
          </Group>

          <Stack gap={4}>
            <Textarea
              autosize
              placeholder={placeholder}
              minRows={3}
              maxRows={3}
              value={message}
              onChange={(event) => setMessage(event.currentTarget.value)}
              error={message.length > 200}
            />
            <Group justify="space-between">
              <Text size="xs" c="red" style={{ visibility: message.length > 200 ? 'visible' : 'hidden' }}>
                Message is too long
              </Text>
              <Text size="xs" c={message.length > 200 ? 'red' : 'dimmed'}>
                {message.length}/200
              </Text>
            </Group>
          </Stack>
        </Stack>
        <Button
          onClick={handleBookAppointment}
          disabled={!effectiveSelectedTime || !professionalProfile || message.length > 200}
          color={primaryColor}
        >
          Book{selectedCount > 0 ? ` (${selectedCount})` : ''}
        </Button>

        {!effectiveSelectedTime && (
          <Alert color="orange" variant="light">
            Select a slot and topic to continue.
          </Alert>
        )}
    </>
  );
};

const BookingAppointmentConfirmationModal = ({
  opened,
  close,
  selectedTopicSelections,
  selectedDate,
  isLoading,
  totalCost,
  onConfirm,
  professionalProfile,
}: {
  opened: boolean;
  close: () => void;
  selectedTopicSelections: TopicSelection[];
  selectedDate: Date;
  isLoading: boolean;
  totalCost: number;
  onConfirm: () => void;
  professionalProfile: ProfileResponseModel | null;
}) => {
  const formattedDate = format(selectedDate, 'EEEE do MMMM yyyy');

  return (
    <Modal
      opened={opened}
      onClose={close}
      title=""
      withCloseButton={false}
      centered
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap={10}>
        <Title order={3} fw="normal" ta="center">
          Confirm Appointments
        </Title>
        <Avatar
          src={getProfilePicture(professionalProfile?.profilePicture)}
          size={80}
          mx="auto"
        />
        <Stack gap={5}>
          <Title order={4} fw="normal" ta="center">
            {professionalProfile?.displayName}
          </Title>
        </Stack>

        {selectedTopicSelections.map((selection) => (
          <Stack key={selection.topicKey} gap={6}>
            <Text fw={700}>{selection.topic?.title || 'Open Session'}</Text>
            <SimpleGrid cols={2} spacing="xs">
              {selection.times.map((time) => (
                <Card key={`${selection.topicKey}-${time.id ?? time.startIso}`} radius="lg" style={{ backgroundColor: '#f5f5f5' }} withBorder={false}>
                  <Stack gap={4} align="center">
                    <Text size="sm" ta="center" c="dimmed">
                      {format(new Date(time.startIso), 'dd MMM yyyy')}
                    </Text>
                    <Text ta="center">{getTimeText(time)}</Text>
                  </Stack>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        ))}

        <Title ta="center" fw="normal" order={5}>
          Total Cost: £{totalCost}
        </Title>
        <Alert title="Important Information" color={primaryColor} radius="lg">
          <List>
            <List.Item>
              Bookings are subject to approval from the Creator.
            </List.Item>
            <List.Item>
              Payment will be possible after the Creator has confirmed the call.
            </List.Item>
          </List>
        </Alert>
        <Group align="center" justify="center">
          <Button variant="outline" w="47%" color={primaryColor} onClick={close}>
            Back
          </Button>
          <Button color={primaryColor} w="47%" onClick={onConfirm} loading={isLoading}>
            Confirm
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export const getTimeText = (time: AvailableTime): string => {
  const startTime = new Date(time.startIso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const endTime = new Date(time.endIso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${startTime} - ${endTime}`;
};

const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
